import { Paper, Stack, Typography, Box, Divider } from '@mui/material';

type Props = {
  adverseLabel?: string;
  expectedLabel?: string;
  favorableLabel?: string;
  adverseValue: number;
  expectedValue: number;
  favorableValue: number;
  unit?: string; // "€/kg" o "€"
  formatValue: (n: number) => string;
};

export default function RangeBar({
  adverseLabel = 'Escenario adverso razonable',
  expectedLabel = 'Escenario esperado',
  favorableLabel = 'Escenario favorable',
  adverseValue,
  expectedValue,
  favorableValue,
  unit,
  formatValue,
}: Props) {
  // Evitar división por 0
  const min = Math.min(adverseValue, expectedValue, favorableValue);
  const max = Math.max(adverseValue, expectedValue, favorableValue);
  const span = Math.max(1e-9, max - min);

  const pos = (v: number) => ((v - min) / span) * 100;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        bgcolor: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.10)',
      }}
    >
      <Stack spacing={1.2}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Variabilidad del margen
        </Typography>

        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Adverso – Esperado – Favorable {unit ? `(${unit})` : ''}
        </Typography>

        {/* Barra */}
        <Box sx={{ position: 'relative', height: 14, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.12)' }}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${Math.min(pos(adverseValue), pos(favorableValue))}%`,
              width: `${Math.abs(pos(favorableValue) - pos(adverseValue))}%`,
              borderRadius: 999,
              bgcolor: 'rgba(255,255,255,0.28)',
            }}
          />
          {/* Marcadores */}
          {[
            { x: pos(adverseValue), title: adverseLabel, v: adverseValue },
            { x: pos(expectedValue), title: expectedLabel, v: expectedValue },
            { x: pos(favorableValue), title: favorableLabel, v: favorableValue },
          ].map((m) => (
            <Box
              key={m.title}
              sx={{
                position: 'absolute',
                left: `calc(${m.x}% - 5px)`,
                top: -3,
                width: 10,
                height: 10,
                borderRadius: 999,
                bgcolor: 'rgba(255,255,255,0.95)',
                boxShadow: '0 0 0 3px rgba(255,255,255,0.15)',
              }}
              title={`${m.title}: ${formatValue(m.v)}${unit ? ` ${unit}` : ''}`}
            />
          ))}
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.10)' }} />

        {/* Etiquetas */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              {adverseLabel}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {formatValue(adverseValue)} {unit || ''}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              {expectedLabel}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {formatValue(expectedValue)} {unit || ''}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              {favorableLabel}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {formatValue(favorableValue)} {unit || ''}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}